import { observer } from 'mobx-react-lite';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { Fragment, useEffect, useCallback, useRef, useState } from 'react';

import type { HeatmapWidgetState } from '../../store/HeatmapWidgetState';
import { HeatmapChartKey } from '../../store/types';
import type { HeatmapSharedHoverState } from '../HeatMapChart/HeatmapChart';
import { HeatmapChart } from '../HeatMapChart/HeatmapChart';
import { clamp } from '../HeatMapChart/utils';

import styles from './heatmap-charts.module.scss';

const CHART_CONFIGS = [
  {
    key: HeatmapChartKey.ROP,
    valueType: 'rop' as const,
    chartId: 'rop' as const,
  },
  {
    key: HeatmapChartKey.MSE,
    valueType: 'mse' as const,
    chartId: 'mse' as const,
  },
  {
    key: HeatmapChartKey.DENSITY,
    valueType: 'density' as const,
    chartId: 'density' as const,
  },
] as const;

type ChartId = (typeof CHART_CONFIGS)[number]['chartId'];

const DEFAULT_PANE_RATIOS: Record<ChartId, number> = {
  rop: 0.34,
  density: 0.33,
  mse: 0.33,
};

const MIN_PANE_RATIO = 0.2;

type SeparatorState = {
  leftId: ChartId;
  rightId: ChartId;
  leftRatio: number;
  rightRatio: number;
  totalVisibleRatio: number;
  startX: number;
};

type HeatmapChartsProps = {
  widgetState: HeatmapWidgetState;
};

export const HeatmapCharts = observer(({ widgetState }: HeatmapChartsProps) => {
  const chartsContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const separatorStateRef = useRef<SeparatorState | null>(null);
  const [paneRatios, setPaneRatios] = useState<Record<ChartId, number>>(() => {
    const ratios = { ...DEFAULT_PANE_RATIOS };
    for (const { chartId } of CHART_CONFIGS) {
      const value = widgetState.statePublisher.params.chartRatios?.[chartId];
      if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        ratios[chartId] = value;
      }
    }
    return ratios;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [sharedHover, setSharedHover] = useState<HeatmapSharedHoverState>({
    dataIndex: null,
    sourceId: null,
  });

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      if (!isDraggingRef.current || !separatorStateRef.current) {
        return;
      }
      const container = chartsContainerRef.current;
      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      if (rect.width === 0) {
        return;
      }

      const { leftId, rightId, leftRatio, rightRatio, totalVisibleRatio, startX } = separatorStateRef.current;
      const pairTotal = leftRatio + rightRatio;
      if (pairTotal === 0) {
        return;
      }

      const deltaPixels = event.clientX - startX;
      const deltaRatio = (deltaPixels / rect.width) * totalVisibleRatio;
      const minVisibleAbsolute = MIN_PANE_RATIO * totalVisibleRatio;
      const effectiveMin = Math.min(minVisibleAbsolute, pairTotal / 2);
      const minLeft = effectiveMin;
      const maxLeft = pairTotal - effectiveMin;
      const nextLeft = clamp(leftRatio + deltaRatio, minLeft, maxLeft);
      const nextRight = pairTotal - nextLeft;

      setPaneRatios((prev) => ({
        ...prev,
        [leftId]: Number(nextLeft.toFixed(4)),
        [rightId]: Number(nextRight.toFixed(4)),
      }));
    };

    const stopDragging = () => {
      if (!isDraggingRef.current) {
        return;
      }
      isDraggingRef.current = false;
      separatorStateRef.current = null;
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDragging);
    window.addEventListener('pointerleave', stopDragging);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopDragging);
      window.removeEventListener('pointerleave', stopDragging);
    };
  }, []);

  useEffect(() => {
    widgetState.statePublisher.debouncedSetParams({
      chartRatios: paneRatios,
    });
  }, [paneRatios, widgetState.statePublisher]);

  const { settings, heatmapChartStore, appParams } = widgetState;
  const visibleCharts = CHART_CONFIGS.filter(({ key }) => settings.isChartVisible(key));
  const totalVisibleRatio = visibleCharts.reduce((sum, chart) => sum + (paneRatios[chart.chartId] ?? 0), 0) || 1;
  const panes = visibleCharts.map((chart) => ({
    ...chart,
    ratio: totalVisibleRatio
      ? (paneRatios[chart.chartId] ?? DEFAULT_PANE_RATIOS[chart.chartId]) / totalVisibleRatio
      : 1,
  }));

  const handleSeparatorPointerDown = (event: ReactPointerEvent<HTMLDivElement>, leftId: ChartId, rightId: ChartId) => {
    event.preventDefault();
    event.stopPropagation();

    const nextTotalVisible = visibleCharts.reduce((sum, chart) => sum + (paneRatios[chart.chartId] ?? 0), 0) || 1;

    isDraggingRef.current = true;
    setIsDragging(true);
    separatorStateRef.current = {
      leftId,
      rightId,
      leftRatio: paneRatios[leftId] ?? DEFAULT_PANE_RATIOS[leftId],
      rightRatio: paneRatios[rightId] ?? DEFAULT_PANE_RATIOS[rightId],
      totalVisibleRatio: nextTotalVisible,
      startX: event.clientX,
    };
  };

  const handleSharedHoverChange = useCallback((dataIndex: number | null, sourceId: string) => {
    setSharedHover((prev) =>
      prev.dataIndex === dataIndex && prev.sourceId === sourceId ? prev : { dataIndex, sourceId },
    );
  }, []);

  const getPaneStyle = (ratio: number) =>
    panes.length > 1
      ? {
          flexBasis: 0,
          flexGrow: ratio,
          flexShrink: 1,
          minWidth: 0,
          height: '100%',
        }
      : {
          flex: '1 1 100%',
          minWidth: 0,
          height: '100%',
        };

  const getZAxisParams = (valueType: 'rop' | 'density' | 'mse') => {
    if (valueType === 'rop') {
      return heatmapChartStore.ropZAxis;
    }
    if (valueType === 'density') {
      return heatmapChartStore.densityZAxis;
    }
    return heatmapChartStore.mseZAxis;
  };

  const getActiveBins = (valueType: 'rop' | 'density' | 'mse') => {
    if (valueType === 'mse') {
      return heatmapChartStore.activeMseBins;
    }

    return heatmapChartStore.activeRopBins;
  };

  return (
    <div
      ref={chartsContainerRef}
      style={{
        flex: 1,
        display: 'flex',
        minHeight: 0,
        gap: 8,
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      {panes.map((pane, index) => {
        const isLastPane = index === panes.length - 1;
        let zAxisLabelKey = settings.currentMseAxisLabelKey;
        if (pane.valueType === 'density') {
          zAxisLabelKey = 'Heatmap-visibility-density';
        } else if (pane.valueType === 'rop') {
          zAxisLabelKey = 'Heatmap-analysis-rop';
        }

        return (
          <Fragment key={pane.chartId}>
            <div style={getPaneStyle(pane.ratio)}>
              <HeatmapChart
                data={heatmapChartStore.data}
                activeBins={getActiveBins(pane.valueType)}
                xAxisValues={heatmapChartStore.xAxisValues}
                yAxisValues={heatmapChartStore.yAxisValues}
                zAxisParams={getZAxisParams(pane.valueType)}
                xAxisParams={settings.currentXAxisParams}
                yAxisParams={settings.currentYAxisParams}
                xAxisLabelKey={settings.currentXAxisLabelKey}
                yAxisLabelKey={settings.currentYAxisLabelKey}
                zAxisLabelKey={zAxisLabelKey}
                isAltNames={widgetState.appParams.isAltNames}
                themeKey={appParams.theme}
                valueField={pane.valueType}
                chartId={pane.chartId}
                sharedHover={sharedHover}
                onSharedHoverChange={handleSharedHoverChange}
                suppressTooltips={widgetState.isSuppressTooltip}
                corridorOverlay={widgetState.corridorOverlayForHeatmap}
              />
            </div>
            {!isLastPane && (
              <div
                className={styles.separator}
                role="separator"
                aria-orientation="vertical"
                onPointerDown={(event) => handleSeparatorPointerDown(event, pane.chartId, panes[index + 1].chartId)}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
});
